/**
 * SystemStatusBadge — Tier 0 module-local company-status pill.
 *
 * Per `.design-incoming/admin-20260522/LIFECYCLE.md §2`, extended by
 * SCRUM-311 to the full 5-status ISA lifecycle. Renders one of five
 * pills (`onboarding` / `training` / `testing` / `active` / `closed`)
 * with the canonical color, icon, and tooltip copy. The status union
 * is sourced from `@artemis/validators` (`ORGANIZATION_STATUSES`) so it
 * cannot drift from the domain tuple.
 *
 * Used wherever a company appears (list rows, detail-page sticky
 * pill, Overview "Recent companies" feed, create-wizard review
 * step, audit entries that reference status).
 *
 * Badge primitive (SCRUM-115) carve-out: the spec calls for
 * composing the kit `Badge` once it ships from PR #143. Until
 * #143 merges, this primitive renders a styled `<span>` using the
 * same token palette the kit Badge will adopt (success/warning/
 * neutral). Pass 2 swaps the `<span>` for
 * `<Badge variant="success|warning|neutral">` on this branch
 * after #143 lands. Same time-bounded kit-promotion carve-out
 * pattern used by SCRUM-73 (LoginPage error region) and SCRUM-113
 * (PortalChooser "Last used" tile).
 *
 * Tooltip is keyboard-accessible per the bundle's
 * `ACCESSIBILITY.md` contract: the pill is focusable
 * (`tabindex={0}`) when `showTooltip` is true, and its
 * `aria-describedby` points at a sibling
 * `<span role="tooltip" id={tooltipId}>` containing the spec
 * tooltip copy. Screen readers therefore announce the tooltip on
 * focus; keyboard-only users can reach it via Tab; mouse users
 * still get the visual reveal via `:hover` / `:focus-visible`
 * CSS. Native `title` is intentionally NOT used — it's hover-only,
 * which the bundle's keyboard-accessibility ask rules out.
 *
 * When `showTooltip` is false the pill drops `tabindex` (so it
 * isn't a tab stop for nothing) and the tooltip element is not
 * rendered; the pill's `aria-label` still names the status so
 * screen readers identify the state.
 */

import * as React from 'react';
import { Icon } from '@sparta/ui-kit';
import { ORGANIZATION_STATUSES } from '@artemis/validators';

// SCRUM-311: the union is DERIVED from the single source of truth
// (`@artemis/validators` → `@artemis/domain`) rather than hand-written,
// so the ISA lifecycle tuple and this status surface can never drift.
// `VISUALS: Record<SystemStatus, BadgeVisual>` below is the completeness
// guard — adding a status to the domain tuple fails typecheck here until
// a visual is supplied.
export type SystemStatus = (typeof ORGANIZATION_STATUSES)[number];

export interface SystemStatusBadgeProps {
  readonly status: SystemStatus;
  readonly size?: 'sm' | 'md';
  readonly showTooltip?: boolean;
  /** ISO string used in the closed-state tooltip; ignored otherwise. */
  readonly closedAt?: string;
}

interface BadgeVisual {
  readonly label: string;
  /** color tokens used until the kit Badge ships — see file-level JSDoc. */
  readonly background: string;
  readonly foreground: string;
  readonly border: string;
  readonly tooltip: (closedAt?: string) => string;
  readonly glyph: React.ReactElement;
}

// SCRUM-199: status glyphs now dispatch to the kit Icon dictionary
// (Lucide-backed) instead of hand-drawn inline SVG. Sizes (12) and the
// heavier stroke weights are preserved; color is inherited via
// currentColor from each pill's `foreground` token (no hardcoded hex).
function SparkleIcon(): React.ReactElement {
  // 'in_testing' rays/spark → closest dictionary match is `spark`.
  return <Icon name="spark" size={12} strokeWidth={2} />;
}

function CheckIcon(): React.ReactElement {
  return <Icon name="check" size={12} strokeWidth={2.5} />;
}

function MinusIcon(): React.ReactElement {
  return <Icon name="minus" size={12} strokeWidth={2.5} />;
}

function BuildingIcon(): React.ReactElement {
  // 'onboarding' — initial company setup/provisioning. Dictionary
  // has no `flag`; `building` is the closest "company being stood up"
  // glyph in the kit Icon dictionary.
  return <Icon name="building" size={12} strokeWidth={2} />;
}

function InfoIcon(): React.ReactElement {
  // 'training' — user-education environment. Dictionary has no `book`;
  // `info` carries the education/guidance semantic and pairs with the
  // `--info-*` color scale.
  return <Icon name="info" size={12} strokeWidth={2} />;
}

const VISUALS: Record<SystemStatus, BadgeVisual> = {
  onboarding: {
    label: 'Onboarding',
    background:
      'color-mix(in oklab, var(--brand-500) 14%, var(--bg-surface))',
    foreground: 'var(--brand-700)',
    border: 'color-mix(in oklab, var(--brand-500) 32%, transparent)',
    tooltip: () => 'Initial setup and client onboarding. No billing.',
    glyph: <BuildingIcon />,
  },
  training: {
    label: 'Training',
    background: 'color-mix(in oklab, var(--info-500) 14%, var(--bg-surface))',
    foreground: 'var(--info-700)',
    border: 'color-mix(in oklab, var(--info-500) 32%, transparent)',
    tooltip: () =>
      'Training environment for user education. Separate from production.',
    glyph: <InfoIcon />,
  },
  testing: {
    label: 'Testing',
    background:
      'color-mix(in oklab, var(--warning-500) 14%, var(--bg-surface))',
    foreground: 'var(--warning-700)',
    border: 'color-mix(in oklab, var(--warning-500) 32%, transparent)',
    tooltip: () => 'Provisioned for verification. No billing.',
    glyph: <SparkleIcon />,
  },
  active: {
    label: 'Active',
    background:
      'color-mix(in oklab, var(--success-500) 14%, var(--bg-surface))',
    foreground: 'var(--success-700)',
    border: 'color-mix(in oklab, var(--success-500) 32%, transparent)',
    tooltip: () => 'Live and operational.',
    glyph: <CheckIcon />,
  },
  closed: {
    label: 'Closed',
    background:
      'color-mix(in oklab, var(--fg-tertiary) 14%, var(--bg-surface))',
    foreground: 'var(--fg-secondary)',
    border: 'color-mix(in oklab, var(--fg-tertiary) 32%, transparent)',
    tooltip: (closedAt) =>
      closedAt !== undefined && closedAt.length > 0
        ? `Permanently shut down on ${closedAt}.`
        : 'Permanently shut down.',
    glyph: <MinusIcon />,
  },
};

export function SystemStatusBadge({
  status,
  size = 'md',
  showTooltip = true,
  closedAt,
}: SystemStatusBadgeProps): React.ReactElement {
  const visual = VISUALS[status];
  const fontSize = size === 'sm' ? '11px' : '12px';
  const padY = size === 'sm' ? '2px' : '3px';
  const padX = size === 'sm' ? '6px' : '8px';
  const tooltip = visual.tooltip(closedAt);
  const generatedId = React.useId();
  const tooltipId = `system-status-tooltip-${generatedId}`;

  return (
    <span
      className="group relative inline-flex"
      // Wrapper enables the visual `:hover` / `:focus-within`
      // tooltip-reveal CSS below (Tailwind `group-*` modifier);
      // doesn't carry semantics.
    >
      <span
        role="status"
        aria-label={visual.label}
        aria-describedby={showTooltip ? tooltipId : undefined}
        tabIndex={showTooltip ? 0 : -1}
        className="inline-flex items-center font-semibold uppercase outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-500)] focus-visible:ring-offset-2"
        style={{
          gap: '4px',
          padding: `${padY} ${padX}`,
          fontSize,
          letterSpacing: '0.4px',
          borderRadius: 'var(--radius-full)',
          background: visual.background,
          color: visual.foreground,
          border: `1px solid ${visual.border}`,
        }}
      >
        <span aria-hidden="true" className="inline-flex">
          {visual.glyph}
        </span>
        {visual.label}
      </span>
      {showTooltip ? (
        <span
          id={tooltipId}
          role="tooltip"
          // Visually hidden by default, revealed on hover or
          // keyboard focus of the parent wrapper. The CSS class
          // chain keeps the tooltip in the DOM (so
          // `aria-describedby` works for screen readers + keyboard
          // tabbing) and reveals it visually on
          // `:hover` / `:focus-within`.
          className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
          style={{
            background: 'var(--fg-primary)',
            color: 'var(--bg-surface)',
            // Force the tooltip to be visible to JSDOM via inline
            // visibility while keeping the visual fade by opacity.
            // (axe-core inspects computed visibility, not opacity,
            // so this keeps the tooltip queryable in tests +
            // production-friendly.)
          }}
        >
          {tooltip}
        </span>
      ) : null}
    </span>
  );
}
